'use client';

import Icon from '@/components/ui/AppIcon';

interface ServiceHours {
  service: string;
  icon: string;
  hours: string;
  timezone: string;
  available247?: boolean;
}

export default function BusinessHoursSection() {
  const serviceHours: ServiceHours[] = [
    {
      service: 'Reception',
      icon: 'UserGroupIcon',
      hours: '24/7',
      timezone: 'IST',
      available247: true,
    },
    {
      service: 'Concierge',
      icon: 'ChatBubbleLeftRightIcon',
      hours: '6:00 AM - 11:00 PM',
      timezone: 'IST',
    },
    {
      service: 'Housekeeping',
      icon: 'HomeIcon',
      hours: '8:00 AM - 8:00 PM',
      timezone: 'IST',
    },
    {
      service: 'Emergency Support',
      icon: 'ExclamationTriangleIcon',
      hours: '24/7',
      timezone: 'IST',
      available247: true,
    },
  ];

  return (
    <section className="py-20 bg-muted">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 text-accent mb-4">
            <Icon name="ClockIcon" size={24} />
            <span className="text-sm font-semibold uppercase tracking-wide">Business Hours</span>
          </div>
          <h2 className="text-3xl lg:text-5xl font-display font-bold text-primary mb-4">
            Service Availability
          </h2>
          <p className="text-text-secondary max-w-2xl mx-auto">
            Our services are available at different times to ensure your comfort and convenience throughout your stay.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            {serviceHours.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 shadow-subtle border border-border"
              >
                <div className="flex items-start space-x-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    service.available247 ? 'bg-success/10' : 'bg-primary/10'
                  }`}>
                    <Icon name={service.icon} size={24} className={service.available247 ? 'text-success' : 'text-accent'} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-display text-lg font-semibold text-text-primary">{service.service}</h4>
                      {service.available247 && (
                        <span className="px-2 py-1 bg-success/10 text-success text-xs font-semibold rounded-full">
                          24/7
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="ClockIcon" size={16} className="text-accent" />
                      <span className="text-text-primary font-medium">{service.hours}</span>
                      <span className="text-text-secondary text-sm">({service.timezone})</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 bg-accent/5 border border-accent/20 rounded-xl p-6">
            <div className="flex items-start space-x-4">
              <Icon name="InformationCircleIcon" size={24} className="text-accent flex-shrink-0" />
              <div>
                <p className="font-semibold text-text-primary mb-2">Response Time Commitment</p>
                <p className="text-text-secondary text-sm">
                  We aim to respond to all inquiries within 2 hours during business hours and within 24 hours for messages received outside business hours. Emergency requests are handled immediately 24/7.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}